<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: DescendantDAbovilleList                               |
| Section: components/Person                                       |
| Purpose: Numbered, interactive d'Aboville descendant list.       |
+------------------------------------------------------------------+
-->
<template>
    <div class="DAbovilleList">
        <div v-if="loading" class="DAbovilleList_status DAbovilleList_status--loading" role="status">
            <LoadingSpinner />
            <span>{{ $t('familySection.dAbovilleLoading') }}</span>
        </div>
        <div v-else-if="error" class="DAbovilleList_status DAbovilleList_status--error" role="alert">
            {{ $t('familySection.dAbovilleLoadError') }}
        </div>
        <div v-else-if="rows.length === 0" class="DAbovilleList_status">
            {{ $t('familySection.dAbovilleEmpty') }}
        </div>
        <div v-else class="DAbovilleList_content">
            <div class="DAbovilleList_context-anchor" aria-hidden="true">
                <ol v-if="activeAncestorRows.length > 0" class="DAbovilleList_context">
                    <li v-for="ancestor in activeAncestorRows" :key="`context-${ancestor.key}`"
                        class="DAbovilleList_context-row"
                        :style="{ '--daboville-depth': ancestor.depth }">
                        <span class="DAbovilleList_number">{{ ancestor.number }}</span>
                        <span class="DAbovilleList_name DAbovilleList_name--static">
                            {{ ancestor.person.name }}
                        </span>
                    </li>
                </ol>
            </div>

            <ol class="DAbovilleList_rows">
                <li v-for="row in rows" :key="row.key" class="DAbovilleList_row"
                    :class="{
                        'DAbovilleList_row--lineage': isLineageRow(row.path),
                        'DAbovilleList_row--active': isActiveRow(row.path),
                        'DAbovilleList_row--root': row.depth === 0,
                        'DAbovilleList_row--hidden-summary': row.isHiddenSummary
                    }"
                    :style="{ '--daboville-depth': row.depth }" tabindex="0"
                    @mouseenter="hoveredPath = row.path" @mouseleave="hoveredPath = null"
                    @focusin="focusedPath = row.path" @focusout="handleRowFocusOut">
                    <button v-if="row.hasDescendants" type="button"
                        class="DAbovilleList_number DAbovilleList_number--toggle"
                        :aria-expanded="String(!isCollapsed(row.path))"
                        :aria-label="$t(isCollapsed(row.path)
                            ? 'familySection.expandDescendants'
                            : 'familySection.collapseDescendants', { number: row.number })"
                        @click.stop="toggleCollapsed(row.path)">
                        <span>{{ row.number }}</span>
                        <span class="DAbovilleList_number-indicator" aria-hidden="true">
                            {{ isCollapsed(row.path) ? '▸' : '▾' }}
                        </span>
                    </button>
                    <span v-else class="DAbovilleList_number">{{ row.number }}</span>

                    <span class="DAbovilleList_person">
                        <a v-if="row.person.profileHref" :href="row.person.profileHref" class="DAbovilleList_name">
                            {{ row.person.name }}
                        </a>
                        <span v-else class="DAbovilleList_name DAbovilleList_name--static">
                            {{ row.person.name }}
                        </span>
                        <span v-if="row.person.gender || row.person.years" class="DAbovilleList_years">
                            <img v-if="row.person.gender" class="DAbovilleList_gender-icon"
                                :src="genderIconSrc(row.person.gender)" :alt="genderAltText(row.person.gender)" />
                            <span v-if="row.person.years">{{ row.person.years }}</span>
                        </span>
                    </span>

                    <span v-if="row.person.profileHref" class="DAbovilleList_actions" role="group"
                        :aria-label="$t('searchResults.actionsAlt')">
                        <a v-if="row.person.relationshipHref" :href="row.person.relationshipHref"
                            class="DAbovilleList_action" :aria-label="$t('searchResults.actions.calculateRelationship')"
                            :data-tooltip="$t('searchResults.actions.calculateRelationship')">
                            <img src="@/assets/icons/cycle-yellow.svg" alt="" aria-hidden="true" />
                        </a>
                        <a :href="row.person.profileHref" class="DAbovilleList_action"
                            :aria-label="$t('searchResults.actions.viewPerson')"
                            :data-tooltip="$t('searchResults.actions.viewPerson')">
                            <img src="@/assets/icons/user.svg" alt="" aria-hidden="true" />
                        </a>
                    </span>
                </li>
            </ol>
        </div>
    </div>
</template>

<script>
import { getDescendants } from '@/api/personService';
import LoadingSpinner from '@/atoms/LoadingSpinner.vue';
import { useAuth } from '@/composables/useAuth';
import { useLocale } from '@/composables/useLocale';
import { extractSortableDate, formatYears } from '@/utils/dateUtils';
import {
    getPersonNodeKey,
    hasLivingPersonAccess,
    isLivingPlaceholder,
    isPrivateLivingPerson
} from '@/utils/personPrivacy';

const MAX_DESCENDANT_GENERATIONS = 7;

export default {
    name: 'DescendantDAbovilleList',
    components: {
        LoadingSpinner
    },
    props: {
        personId: {
            type: String,
            required: true
        }
    },
    setup() {
        const { authToken } = useAuth();
        const { locale, localePath } = useLocale();

        return {
            authToken,
            locale,
            localePath
        };
    },
    data() {
        return {
            rootPerson: null,
            loading: false,
            error: '',
            requestId: 0,
            abortController: null,
            hoveredPath: null,
            focusedPath: null,
            collapsedPaths: []
        };
    },
    computed: {
        rows() {
            if (!this.rootPerson) {
                return [];
            }

            const rows = [];
            this.appendPersonRows(this.rootPerson, [1], 0, rows);
            const expandedRows = rows.filter(row => !this.isHiddenByCollapsedAncestor(row.path));
            return this.collapseHiddenRows(expandedRows);
        },
        activePath() {
            return this.hoveredPath || this.focusedPath;
        },
        activeAncestorRows() {
            if (!this.hoveredPath) {
                return [];
            }

            return this.rows.filter(row => this.isAncestorPath(row.path, this.hoveredPath));
        }
    },
    watch: {
        personId: {
            immediate: true,
            handler() {
                this.fetchDescendants();
            }
        }
    },
    beforeUnmount() {
        this.abortController?.abort?.();
    },
    methods: {
        async fetchDescendants() {
            const requestId = this.requestId + 1;
            this.requestId = requestId;
            this.abortController?.abort?.();
            this.abortController = typeof AbortController !== 'undefined' ? new AbortController() : null;
            this.loading = true;
            this.error = '';
            this.hoveredPath = null;
            this.focusedPath = null;
            this.collapsedPaths = [];

            try {
                const data = await getDescendants(this.personId, MAX_DESCENDANT_GENERATIONS - 1, this.abortController
                    ? { signal: this.abortController.signal }
                    : {});

                if (requestId !== this.requestId) {
                    return;
                }

                if (!data || typeof data !== 'object') {
                    throw new Error('Invalid descendant response');
                }

                this.rootPerson = data;
            } catch (error) {
                if (requestId !== this.requestId || error?.name === 'CanceledError' || error?.name === 'AbortError') {
                    return;
                }

                console.error('Error loading d’Aboville descendant list:', error);
                this.rootPerson = null;
                this.error = this.$t('familySection.dAbovilleLoadError');
            } finally {
                if (requestId === this.requestId) {
                    this.loading = false;
                }
            }
        },
        appendPersonRows(person, path, depth, rows) {
            const number = path.join('.');
            const children = depth >= MAX_DESCENDANT_GENERATIONS
                ? []
                : this.sortChildren(person?.descendants);
            rows.push({
                key: `${number}-${getPersonNodeKey(person, 'unknown')}`,
                number,
                path,
                depth,
                hasDescendants: children.length > 0,
                person: this.toDisplayPerson(person)
            });

            children.forEach((child, index) => {
                this.appendPersonRows(child, [...path, index + 1], depth + 1, rows);
            });
        },
        toggleCollapsed(path) {
            const number = path.join('.');
            this.collapsedPaths = this.collapsedPaths.includes(number)
                ? this.collapsedPaths.filter(collapsedNumber => collapsedNumber !== number)
                : [...this.collapsedPaths, number];
        },
        isCollapsed(path) {
            return this.collapsedPaths.includes(path.join('.'));
        },
        isHiddenByCollapsedAncestor(path) {
            const number = path.join('.');
            return this.collapsedPaths.some(collapsedNumber => number.startsWith(`${collapsedNumber}.`));
        },
        collapseHiddenRows(rows) {
            if (hasLivingPersonAccess(this.authToken)) {
                return rows;
            }

            const collapsedRows = [];
            let index = 0;

            while (index < rows.length) {
                if (!rows[index].person.isPrivacyHidden) {
                    collapsedRows.push(rows[index]);
                    index += 1;
                    continue;
                }

                let runEnd = index + 1;
                while (runEnd < rows.length && rows[runEnd].person.isPrivacyHidden) {
                    runEnd += 1;
                }

                const hiddenRun = rows.slice(index, runEnd);
                if (hiddenRun.length <= 2) {
                    collapsedRows.push(...hiddenRun);
                } else {
                    const firstRow = hiddenRun[0];
                    const lastRow = hiddenRun[hiddenRun.length - 1];
                    collapsedRows.push({
                        key: `hidden-${firstRow.key}-${lastRow.key}`,
                        number: `${firstRow.number}–${lastRow.number}`,
                        path: firstRow.path,
                        depth: Math.min(...hiddenRun.map(row => row.depth)),
                        isHiddenSummary: true,
                        person: {
                            name: this.$t('familySection.hiddenPeopleCount', { count: hiddenRun.length }),
                            years: '',
                            gender: '',
                            personID: '',
                            profileHref: '',
                            relationshipHref: '',
                            isPrivacyHidden: true
                        }
                    });
                }

                index = runEnd;
            }

            return collapsedRows;
        },
        sortChildren(children) {
            return (Array.isArray(children) ? children : [])
                .map((person, index) => ({ person, index, date: this.getSortDate(person) }))
                .sort((a, b) => {
                    if (a.date && b.date) return a.date - b.date || a.index - b.index;
                    if (a.date) return -1;
                    if (b.date) return 1;
                    return a.index - b.index;
                })
                .map(item => item.person);
        },
        getSortDate(person) {
            const date = extractSortableDate(person?.birthdatetr || person?.birthdate)
                || extractSortableDate(person?.altbirthdatetr || person?.altbirthdate);
            return date ? date.getTime() : null;
        },
        toDisplayPerson(person) {
            const placeholder = isLivingPlaceholder(person);
            const privateLiving = isPrivateLivingPerson(person, this.authToken);
            const isPrivacyHidden = !hasLivingPersonAccess(this.authToken) && (placeholder || privateLiving);
            const rawName = `${person?.firstname || ''} ${person?.lastname || ''}`.trim()
                || person?.name
                || '';
            const name = placeholder
                ? this.$t('privacy.livingPlaceholder')
                : privateLiving
                    ? this.$t('nav.privacyHidden')
                    : rawName || this.$t('familySection.unknownPerson');
            const canOpenProfile = Boolean(person?.personID && !placeholder && !privateLiving && rawName);
            const personID = canOpenProfile ? String(person.personID) : '';
            const encodedPersonID = personID ? encodeURIComponent(personID) : '';
            const profileHref = personID
                ? this.localePath(`/person/${encodedPersonID}`)
                : '';
            const hasBranch = person?.hasBranch === true || Number(person?.hasBranch) === 1;
            const relationshipHref = personID && personID.toUpperCase() !== 'I43' && !hasBranch
                ? this.localePath(`/relationship/I43/${encodedPersonID}?source=person&personId=${encodedPersonID}`)
                : '';
            const normalizedGender = String(person?.sex || '').toUpperCase();

            return {
                name,
                years: placeholder || privateLiving ? '' : this.formatPersonYears(person),
                gender: placeholder || privateLiving || !['M', 'F'].includes(normalizedGender)
                    ? ''
                    : normalizedGender,
                personID,
                profileHref,
                relationshipHref,
                isPrivacyHidden
            };
        },
        genderIconSrc(gender) {
            if (gender === 'M') return require('@/assets/icons/male.svg');
            if (gender === 'F') return require('@/assets/icons/female.svg');
            return '';
        },
        genderAltText(gender) {
            if (gender === 'M') return this.$t('personHeader.gender.male');
            if (gender === 'F') return this.$t('personHeader.gender.female');
            return '';
        },
        formatPersonYears(person) {
            if (!person) return '';

            return person.years || formatYears(
                person.birthdatetr || person.birthdate,
                person.altbirthdatetr || person.altbirthdate,
                person.deathdatetr || person.deathdate,
                person.burialdatetr || person.burialdate,
                person.living,
                this.locale
            );
        },
        pathsShareLineage(firstPath, secondPath) {
            if (!firstPath || !secondPath) return false;
            const comparableLength = Math.min(firstPath.length, secondPath.length);

            for (let index = 0; index < comparableLength; index += 1) {
                if (firstPath[index] !== secondPath[index]) return false;
            }

            return true;
        },
        isAncestorPath(ancestorPath, descendantPath) {
            return ancestorPath.length < descendantPath.length
                && ancestorPath.every((value, index) => value === descendantPath[index]);
        },
        isLineageRow(path) {
            return Boolean(this.activePath && this.pathsShareLineage(path, this.activePath));
        },
        isActiveRow(path) {
            return Boolean(this.activePath
                && path.length === this.activePath.length
                && path.every((value, index) => value === this.activePath[index]));
        },
        handleRowFocusOut(event) {
            if (!event.currentTarget.contains(event.relatedTarget)) {
                this.focusedPath = null;
            }
        }
    }
};
</script>

<style scoped>
.DAbovilleList {
    background: transparent;
    color: var(--brand-text-light);
    font-family: Inter, sans-serif;
}

.DAbovilleList_content {
    position: relative;
}

.DAbovilleList_context-anchor {
    position: sticky;
    top: 0;
    z-index: 4;
    height: 0;
    pointer-events: none;
}

.DAbovilleList_context {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    margin: 0;
    padding: 4px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.24);
    background: var(--brand-bg-8);
    box-shadow: 0 10px 18px rgba(18, 8, 4, 0.2);
    list-style: none;
}

.DAbovilleList_context-row {
    display: grid;
    grid-template-columns: minmax(70px, auto) minmax(0, 1fr);
    align-items: baseline;
    gap: 12px;
    padding: 4px 14px 4px calc(14px + var(--daboville-depth) * 24px);
    background: rgba(255, 204, 51, 0.08);
}

.DAbovilleList_context-row .DAbovilleList_name {
    overflow: hidden;
    font-size: 0.875rem;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.DAbovilleList_rows {
    width: 100%;
    margin: 0;
    padding: 0;
    list-style: none;
    border-top: 1px solid rgba(255, 255, 255, 0.24);
    border-bottom: 1px solid rgba(255, 255, 255, 0.24);
}

.DAbovilleList_row {
    display: grid;
    grid-template-columns: minmax(70px, auto) minmax(0, 1fr) 128px;
    align-items: center;
    gap: 12px;
    padding: 9px 14px 9px calc(14px + var(--daboville-depth) * 24px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
    outline: none;
    transition: background-color 0.12s ease;
}

.DAbovilleList_row:last-child {
    border-bottom: 0;
}

.DAbovilleList_row--root {
    font-weight: 700;
}

.DAbovilleList_row--lineage {
    background: rgba(255, 204, 51, 0.08);
}

.DAbovilleList_row--active {
    background: rgba(255, 204, 51, 0.16);
}

.DAbovilleList_row:focus-visible {
    box-shadow: inset 3px 0 var(--brand-color-3);
}

.DAbovilleList_row--hidden-summary {
    color: rgba(255, 255, 255, 0.72);
}

.DAbovilleList_number {
    color: rgba(255, 255, 255, 0.74);
    font-size: 0.875rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    overflow-wrap: anywhere;
}

.DAbovilleList_number--toggle {
    display: inline-flex;
    align-items: baseline;
    justify-content: flex-start;
    gap: 6px;
    width: fit-content;
    padding: 0;
    border: 0;
    background: transparent;
    font-family: inherit;
    cursor: pointer;
}

.DAbovilleList_number--toggle:hover,
.DAbovilleList_number--toggle:focus-visible {
    color: var(--brand-color-3);
}

.DAbovilleList_number--toggle:focus-visible {
    outline: 2px solid var(--brand-color-3);
    outline-offset: 3px;
}

.DAbovilleList_number-indicator {
    font-size: 0.75rem;
}

.DAbovilleList_person {
    min-width: 0;
    line-height: 1.55;
}

.DAbovilleList_name {
    color: var(--brand-text-light);
    font-size: 1rem;
    font-weight: 600;
    text-decoration: none;
}

a.DAbovilleList_name:hover,
a.DAbovilleList_name:focus-visible {
    color: var(--brand-color-3);
    text-decoration: underline;
}

.DAbovilleList_years {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-left: 6px;
    color: rgba(255, 255, 255, 0.72);
    font-size: 0.875rem;
}

.DAbovilleList_gender-icon {
    width: 12px;
    max-height: 13px;
    flex: 0 0 auto;
    filter: brightness(0) invert(1);
    opacity: 0.72;
}

.DAbovilleList_actions {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    width: 128px;
    padding-right: 58px;
    opacity: 0;
    pointer-events: none;
    transform: translateX(6px);
    transition: opacity 0.12s ease, transform 0.12s ease;
}

.DAbovilleList_row:hover .DAbovilleList_actions,
.DAbovilleList_row:focus-within .DAbovilleList_actions {
    opacity: 1;
    pointer-events: auto;
    transform: translateX(0);
}

.DAbovilleList_action {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    flex: 0 0 32px;
    border: 1px solid rgba(255, 255, 255, 0.28);
    border-radius: 50%;
    background: rgba(29, 29, 29, 0.42);
    transition: background-color 0.12s ease, border-color 0.12s ease, box-shadow 0.12s ease;
}

.DAbovilleList_action:hover,
.DAbovilleList_action:focus-visible {
    border-color: var(--brand-color-3);
    background: var(--brand-color-3);
    box-shadow: 0 0 0 3px rgba(255, 204, 51, 0.2);
    outline: none;
}

.DAbovilleList_action img {
    width: 14px;
    max-height: 16px;
    filter: brightness(0) invert(1);
}

.DAbovilleList_action:hover img,
.DAbovilleList_action:focus-visible img {
    filter: brightness(0);
}

.DAbovilleList_action::after {
    position: absolute;
    right: 0;
    bottom: calc(100% + 8px);
    z-index: 6;
    padding: 6px 8px;
    border-radius: 4px;
    background: #1d1d1d;
    color: #fff;
    content: attr(data-tooltip);
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1.2;
    opacity: 0;
    pointer-events: none;
    transform: translateY(3px);
    transition: opacity 0.12s ease, transform 0.12s ease;
    white-space: nowrap;
}

.DAbovilleList_action:hover::after,
.DAbovilleList_action:focus-visible::after {
    opacity: 1;
    transform: translateY(0);
}

.DAbovilleList_status {
    display: grid;
    place-items: center;
    min-height: 240px;
    padding: 32px;
    color: rgba(255, 255, 255, 0.82);
    font-family: inherit;
    text-align: center;
}

.DAbovilleList_status--error {
    color: #ffd6d6;
}

.DAbovilleList_status--loading {
    align-content: center;
    gap: 8px;
}

.DAbovilleList_status--loading :deep(.loader) {
    height: auto;
}

.DAbovilleList_status--loading :deep(.path) {
    stroke: var(--brand-color-3);
}

@media (max-width: 760px) {
    .DAbovilleList_row,
    .DAbovilleList_context-row {
        grid-template-columns: minmax(58px, auto) minmax(0, 1fr);
        gap: 8px;
        padding: 8px 8px 8px calc(8px + var(--daboville-depth) * 11px);
    }

    .DAbovilleList_context-row {
        padding-top: 3px;
        padding-bottom: 3px;
    }

    .DAbovilleList_actions {
        display: none;
    }
}
</style>
